import React, { useCallback, useEffect, useState } from "react";
import { bindActionCreators } from "redux";
import { connect, useDispatch, useSelector } from "react-redux";
import InlineEditField from "./InlineEditField";
import CompanyLogoSection from "./CompanyLogoSection";
import BusinessLocationSection from "./BusinessLocationSection";
import TimeZoneSection from "./TimeZoneSection";
import LogoUploadModal from "./LogoUploadModal";
import ServiceLocationSection from "./ServiceLocationSection";
import SkillsetsSection from "./SkillsetsSection";
import { getFieldValidationError, buildOrgProfilePayload } from "./orgProfileHelpers";
import {
  getOrgLocationByZip,
  fetchServiceLocData,
  addOrRemoveServiceLocNames,
  removeOrgLogo,
  fetchAllSkilset,
  fetchZipData,
} from "../../../containers/AdminPanel/actions/action";
import {
  fetchOrgProfileRequest,
  updateOrgProfileRequest,
  saveSkillset,
} from "../../../containers/AdminPanel/orgProfile/orgProfileSlice";
import useLiveRefresh from "@utils/useLiveRefresh";
import { useModules } from "../../../utils/modules";
import Toast from '../../../components/Toast';

const IMG_LOADING_TIMEOUT_MS = 10000;

const OrgProfile = (props) => {
  const { isModuleEnabled } = useModules();
  const dispatch = useDispatch();
  const { tzData } = useSelector((state) => state.sessionContextOidc);
  const { orgSkillSets } = useSelector((state) => state.adminPanel.orgProfileData);
  const [skillSetData, setSkillSetData] = useState([]);

  const [editFieldDataArray, setEditFieldDataArray] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [OrgProfileFieldValue, setOrgProfileFieldValue] = useState({});
  const [isModal, setIsModal] = useState(false);
  const [isConfirmation, setIsConfirmation] = useState(false);
  const [isImgLoading, setIsImgLoading] = useState(false);
  const [locationNames, setLocationNames] = useState([]);
  const [zipLocData, setZipLocData] = useState([]);
  const [inputChange, setInputChange] = useState([]);
  const [selectedTimeZone, setSelectedTimeZone] = useState("");
  const [orgProfileUIData, setOrgProfileUIData] = useState({});

  const toast = useSelector((state) =>  state.toast);
  const orgProfileInfo = useSelector((state) => state.adminPanel.orgProfileDetail?.orgProfileInfo);
  const zipLocSuggestion = useSelector((state) => state.adminPanel.orgProfileData?.zipLocSuggestion);
  const { updatingData, hostName } = props.data || {};

  useEffect(() => {
    dispatch(fetchOrgProfileRequest());
  }, [dispatch]);

  // organization.* covers the profile, notes settings, skillsets and service
  // locations, all of which this screen shows.
  useLiveRefresh(["organization"], () => {
    dispatch(fetchOrgProfileRequest());
  });

  useEffect(() => {
    const sourceData = orgProfileInfo || props.data?.orgProfileData;

    if (sourceData && Object.keys(sourceData).length > 0) {
      setOrgProfileUIData(sourceData);
      setSelectedTimeZone((sourceData.OrgTimeZoneTz || "").toLowerCase());

      if (window?.$) {
        window
          .$("#profileNotification")
          [sourceData.IsProfileComplete ? "hide" : "show"]();
      }
    }
  }, [orgProfileInfo, props.data]);  

  useEffect(() => {
    dispatch(fetchAllSkilset());
  }, []);

  // The loader is cleared by the logo's onLoad/onError, which never fire when
  // the logo URL does not change (a failed update, say), so it cannot outlast this.
  useEffect(() => {
    if (!isImgLoading) return undefined;
    const timer = setTimeout(() => setIsImgLoading(false), IMG_LOADING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isImgLoading]);

  const profileFullImage = orgProfileUIData?.ProfileFullImage || "";
  const isDefaultProfileImage = profileFullImage.includes("org.png");

  useEffect(() => {
    if (orgProfileInfo?.OrgSkillsList?.length > 0) {
      setSkillSetData(
        orgProfileInfo.OrgSkillsList.map((item) => ({
          id: item.skill_id,
          name: item.skill_name,
        })),
      );
    }else{
      setSkillSetData([]);
    }
  }, [orgProfileInfo?.OrgSkillsList]);

  // VALIDATION
  const validateOrgProfileField = useCallback(
    (key, override) => {
      const value = (override ?? OrgProfileFieldValue[key] ?? "").trim();
      const error = getFieldValidationError(key, value, orgProfileInfo);

      if (error) {
        setValidationErrors((prev) => ({ ...prev, [key]: error }));
        return false;
      }

      setValidationErrors((prev) => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });

      return true;
    },
    [OrgProfileFieldValue, orgProfileInfo],
  );

  // INPUT HANDLING
  const setValue = useCallback(
    ({ id, updatedValue }) => {
      setOrgProfileFieldValue((prev) => ({
        ...prev,
        [id]: updatedValue,
      }));

      validateOrgProfileField(id, updatedValue);

      setInputChange((prev) => {
        const exists = prev.includes(id);
        if (updatedValue.trim() && !exists) return [...prev, id];
        if (!updatedValue.trim()) return prev.filter((i) => i !== id);
        return prev;
      });
    },
    [validateOrgProfileField],
  );

  const closeEditField = useCallback((name) => {
    setEditFieldDataArray((prev) => prev.filter((i) => i !== name));

    setOrgProfileFieldValue((prev) => {
      const copy = { ...prev };
      delete copy[name];
      return copy;
    });

    setInputChange((prev) => prev.filter((i) => i !== name));

    setValidationErrors((prev) => {
      const copy = { ...prev };
      delete copy[name];
      return copy;
    });
  }, []);

  const onEditClick = useCallback(({ id, value }) => {
    setEditFieldDataArray((prev) => [...prev, id]);
    setOrgProfileFieldValue((prev) => ({ ...prev, [id]: value }));
  }, []);

  // UPDATE
  const buildPayload = useCallback(
    (overrides = {}) =>
      buildOrgProfilePayload({
        fieldValues: OrgProfileFieldValue,
        orgProfileInfo,
        selectedTimeZone,
        overrides,
      }),
    [OrgProfileFieldValue, orgProfileInfo, selectedTimeZone],
  );

  const updateOrgProfile = useCallback(
    (key, overrides = {}) => {
      if (key && !inputChange.includes(key)) return;
      if (key && !validateOrgProfileField(key)) return;

      const payload = buildPayload({
        ...overrides,
        id: orgProfileUIData?.OrganizationId,
      });
      dispatch(updateOrgProfileRequest(payload));
      if (key) closeEditField(key);
    },
    [
      inputChange,
      validateOrgProfileField,
      buildPayload,
      dispatch,
      closeEditField,
    ],
  );

  const handleUpdateTagForOrg = (tag) => {
    const namesInSkillset = new Set(skillSetData.map(item => item.name));
    const result = tag.filter(item => item.name && !namesInSkillset.has(item.name));
    const payload = { data: result };
    dispatch(saveSkillset(payload));
  };

  // OTHERS
  const handleCloseToast = () => dispatch({ type: 'HIDE_TOAST' });

  const setOrgProfileTimeZone = useCallback(
    (tz) => {
      setSelectedTimeZone(tz);
      updateOrgProfile(null, { timeZoneTz: tz });
    },
    [updateOrgProfile],
  );

  const addLogoHandler = (result) => {
    const { guid, file_name } = result;
    const extension = file_name.split(".").pop();
    const enrichedGuid = `${guid}.${extension}`;

    if (guid) {
      updateOrgProfile(null, { profileImage: enrichedGuid });
    }

    setIsModal(false);
    setIsImgLoading(true);
  };

  const removeLogoHandler = () => {
    updateOrgProfile(null, { profileImage: null });
    setIsConfirmation(false);
  };

  const zipData = (data) => setZipLocData(data);

  const updateBusinessLocation = useCallback(
    (payload) => {
      updateOrgProfile(null, {
        addressLine1: payload.address1,
        addressLine2: payload.address2,
        city: payload.city,
        state: payload.state,
        country: payload.country,
        zip: payload.zip,
        county: payload.county,
      });
      closeEditField("businessLocation");
    },
    [updateOrgProfile, closeEditField],
  );

  if (orgProfileUIData != null && !Object.keys(orgProfileUIData).length) {
    return null;
  }

  const fetchServiceLocHandler = (data) => {
    props.fetchServiceLocData({
      queryParam: data.queryParam,
      countryCode: data.countryCode,
      callback: setLocationNames,
    });
  }

  const fetchZipDataHandler = (data) => {
    props.fetchLocationZipData({
      ...data,
      callback: zipData,
    });
  }

  const renderInlineField = ({
    fieldKey,
    labelText,
    labelClassName,
    spanClassName,
    label,
    displayValue,
    maxLength,
    suffix,
    showAddWhenEmpty,
    dataTestId,
  }) => (
    <div className="form-row mb__18">
      <label className={labelClassName}>{labelText}</label>
      <span className={spanClassName}>
        <InlineEditField
          fieldKey={fieldKey}
          label={label}
          isUpdating={updatingData === fieldKey}
          isEditing={editFieldDataArray.includes(fieldKey)}
          isSaveEnabled={inputChange.includes(fieldKey)}
          validationError={validationErrors[fieldKey]}
          inputValue={OrgProfileFieldValue[fieldKey]}
          displayValue={displayValue}
          maxLength={maxLength}
          suffix={suffix}
          showAddWhenEmpty={showAddWhenEmpty}
          onChange={(value) => setValue({ id: fieldKey, updatedValue: value })}
          onSave={() => updateOrgProfile(fieldKey)}
          onCancel={() => closeEditField(fieldKey)}
          onEdit={(value) => onEditClick({ id: fieldKey, value })}
          dataTestId={dataTestId}
        />
      </span>
    </div>
  );

  return (
    <div id="admin-content" className="profileSettingContainer">
      <LogoUploadModal
        isModal={isModal}
        isConfirmation={isConfirmation}
        setIsModal={setIsModal}
        setIsConfirmation={setIsConfirmation}
        onImageSuccess={addLogoHandler}
        onImageRemove={removeLogoHandler}
      />

      <div className="profile-settings">
        <div className="colFlex admin__header mb">
          <h1>Profile Settings</h1>
        </div>

        {isModuleEnabled("admin.profile_setting.company_name") &&
          renderInlineField({
            fieldKey: "companyName",
            labelText: "Company Name",
            labelClassName: "col-md-3",
            spanClassName: "col-md-5 rowFlex alignMiddle",
            displayValue: orgProfileUIData?.OrganizationName,
            maxLength: 200,
            dataTestId: "details editCompanyName",
          })
        }

        {isModuleEnabled("admin.profile_setting.company_logo") && (
          <CompanyLogoSection
            profileFullImage={profileFullImage}
            isDefaultProfileImage={isDefaultProfileImage}
            isImgLoading={isImgLoading}
            setIsImgLoading={setIsImgLoading}
            setIsModal={setIsModal}
            setIsConfirmation={setIsConfirmation}
          />
        )}

        {isModuleEnabled("admin.profile_setting.business_location") && (
          <div className="form-row mb__18">
            <label className="gridEven col-md-3">Business Location</label>
            <BusinessLocationSection
              updatingData={updatingData}
              isEditing={editFieldDataArray.includes("businessLocation")}
              orgProfileUIData={orgProfileUIData}
              orgProfileInfo={orgProfileInfo}
              onEditClick={onEditClick}
              closeEditField={closeEditField}
              updateBusinessLocation={updateBusinessLocation}
              setInputChange={setInputChange}
              inputChange={inputChange}
              getOrgLocationByZip={props.getOrgLocationByZip}
              zipLocSuggestion={zipLocSuggestion}
            />
        </div>
        )}

        {isModuleEnabled("admin.profile_setting.time_zone") && (
          <TimeZoneSection
            tzData={tzData}
            selectedTimeZone={selectedTimeZone}
            setOrgProfileTimeZone={setOrgProfileTimeZone}
          />
        )}

        {isModuleEnabled("admin.profile_setting.service_location") && (
          <ServiceLocationSection
            orgProfileUIData={orgProfileUIData}
            zipLocData={zipLocData}
            locationNames={locationNames}
            fetchServiceLocData={fetchServiceLocHandler}
            fetchZipData={fetchZipDataHandler}
            addOrRemoveServiceLocNames={props.addOrRemoveServiceLocNames}
          />
        )}

        {isModuleEnabled("admin.profile_setting.primary_business") &&
          <div className="form-row mb__18">
            <label className="gridEven col-md-3">Primary Business</label>
            <span className="gridOdd col-md-9">{orgProfileUIData?.BusinessType || "-"}</span>
          </div>
        }

        {isModuleEnabled("admin.profile_setting.skillsets") && (
          <SkillsetsSection
            skillSetData={skillSetData}
            orgSkillSets={orgSkillSets}
            handleUpdateTagForOrg={handleUpdateTagForOrg}
          />
        )}

        {isModuleEnabled("admin.profile_setting.sub_domain") &&
          renderInlineField({
            fieldKey: "subDomain",
            labelText: "Sub-domain",
            labelClassName: "gridEven col-md-3",
            spanClassName: "gridOdd alignMiddle col-md-5",
            displayValue: orgProfileUIData?.Subdomain,
            maxLength: 60,
            suffix: <span className="domain">{hostName}</span>,
            dataTestId: "details editSubdomain",
          })
        }

        {isModuleEnabled("admin.profile_setting.phone") &&
          renderInlineField({
            fieldKey: "phone",
            labelText: "Phone",
            labelClassName: "col-md-3",
            spanClassName: "col-md-5 rowFlex",
            displayValue: orgProfileUIData?.Phone,
            maxLength: 20,
            showAddWhenEmpty: true,
            dataTestId: "details editPhone",
          })
        }

        {isModuleEnabled("admin.profile_setting.fax") &&
          renderInlineField({
            fieldKey: "fax",
            labelText: "Fax",
            labelClassName: "col-md-3",
            spanClassName: "col-md-5 rowFlex",
            displayValue: orgProfileUIData?.Fax,
            maxLength: 12,
            showAddWhenEmpty: true,
            dataTestId: "details editFax",
          })
        }
      </div>

      {toast.show &&
        <Toast
          message={toast.message}
          type={toast.type}
          delayTime={toast.delayTime}
          show={toast.show}
          iconType={toast.iconType}
          autoClose={toast.autoClose}
          closeToast={handleCloseToast}
        />
      }
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  getOrgLocationByZip: bindActionCreators(getOrgLocationByZip, dispatch),
  fetchServiceLocData: bindActionCreators(fetchServiceLocData, dispatch),
  fetchLocationZipData: bindActionCreators(fetchZipData, dispatch),
  addOrRemoveServiceLocNames: bindActionCreators(addOrRemoveServiceLocNames, dispatch),
  removeOrgLogo: bindActionCreators(removeOrgLogo, dispatch),
});

export default connect(null, mapDispatchToProps)(OrgProfile);
